import { supabase } from '../config/supabase';
import type { BankAccount } from '../types/account';

export const getAllAccounts = async (): Promise<BankAccount[]> => {
  const { data, error } = await supabase
    .from('accounts')
    .select('*')
    .order('created_at', { ascending: false });
    
  if (error) {
    console.error('Error fetching accounts:', error);
    return [];
  }
  return data.map(mapAccountFromDB);
};

export const createAccount = async (
  data: Omit<BankAccount, 'id' | 'createdAt' | 'updatedAt'>
): Promise<BankAccount | null> => {
  const { data: result, error } = await supabase
    .from('accounts')
    .insert({
      bank_name: data.bankName,
      account_name: data.accountName,
      account_type: data.accountType,
      balance: data.balance,
      currency: data.currency,
      account_number_last4: data.accountNumberLast4,
      color: data.color,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating account:', error);
    return null;
  }
  return mapAccountFromDB(result);
};

export const updateAccount = async (
  id: string,
  updates: Partial<Omit<BankAccount, 'id' | 'createdAt'>>
): Promise<BankAccount | null> => {
  const dbUpdates: any = {};
  if (updates.bankName !== undefined) dbUpdates.bank_name = updates.bankName;
  if (updates.accountName !== undefined) dbUpdates.account_name = updates.accountName;
  if (updates.accountType !== undefined) dbUpdates.account_type = updates.accountType;
  if (updates.balance !== undefined) dbUpdates.balance = updates.balance;
  if (updates.currency !== undefined) dbUpdates.currency = updates.currency;
  if (updates.accountNumberLast4 !== undefined) dbUpdates.account_number_last4 = updates.accountNumberLast4;
  if (updates.color !== undefined) dbUpdates.color = updates.color;

  const { data, error } = await supabase
    .from('accounts')
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating account:', error);
    return null;
  }
  return mapAccountFromDB(data);
};

export const deleteAccount = async (id: string): Promise<boolean> => {
  const { error } = await supabase.from('accounts').delete().eq('id', id);
  if (error) {
    console.error('Error deleting account:', error);
    return false;
  }
  return true;
};

const mapAccountFromDB = (row: any): BankAccount => ({
  id: row.id,
  bankName: row.bank_name,
  accountName: row.account_name,
  accountType: row.account_type,
  balance: Number(row.balance),
  currency: row.currency,
  accountNumberLast4: row.account_number_last4,
  color: row.color,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});
